import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /** @type {HTMLElement[]} */
  #benefitPopovers = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.classList.contains('announcement-bar__slider--benefits')) {
      this.#benefitPopovers = Array.from(this.querySelectorAll('.dietz-benefit__panel[popover]'));
      this.#benefitPopovers.forEach((popover) => document.body.append(popover));
    }

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('focusin', this.suspend);
    this.removeEventListener('focusout', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.suspend();
    this.#benefitPopovers.forEach((popover) => popover.remove());
    this.#benefitPopovers = [];
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    this.#benefitPopovers.forEach((popover) => {
      if (popover.matches(':popover-open')) popover.hidePopover();
    });

    if (this.classList.contains('announcement-bar__slider--benefits')) {
      const slideCount = (this.refs.slides ?? []).length;
      const rotationStep = slideCount > 0 ? 360 / slideCount : 90;

      this.querySelector('.announcement-bar__slides')?.style.setProperty(
        '--dietz-cube-rotation',
        `${current * -rotationStep}deg`
      );
    }

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      const isHidden = index !== relativeIndex;
      slide.setAttribute('aria-hidden', `${isHidden}`);

      if (isHidden) {
        slide.querySelectorAll('details[open]').forEach((details) => details.removeAttribute('open'));
        slide.querySelectorAll('[popover]:popover-open').forEach((popover) => popover.hidePopover());
      }
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

const DIETZ_PROMO_COUNTDOWN_STORAGE_KEY = 'dietz-promo-countdown-end-v2';
const DIETZ_PROMO_COUNTDOWN_DURATION = 46 * 60 * 1000 + 35 * 1000;

class DietzPromoCountdown extends HTMLElement {
  /** @type {number | undefined} */
  #frameId;

  #endAt = 0;
  #lastAccessibleSecond = -1;

  /** @type {Record<string, HTMLElement | null>} */
  #parts = {};

  connectedCallback() {
    this.#parts = {
      hours: this.querySelector('[data-countdown-hours]'),
      minutes: this.querySelector('[data-countdown-minutes]'),
      seconds: this.querySelector('[data-countdown-seconds]'),
    };

    this.#endAt = this.#getOrCreateEndTime();
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#start();
  }

  disconnectedCallback() {
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#stop();
  }

  get duration() {
    const configuredDuration = Number(this.dataset.duration);
    return Number.isFinite(configuredDuration) && configuredDuration > 0
      ? configuredDuration
      : DIETZ_PROMO_COUNTDOWN_DURATION;
  }

  #getOrCreateEndTime() {
    const now = Date.now();

    try {
      const storedEndTime = Number(sessionStorage.getItem(DIETZ_PROMO_COUNTDOWN_STORAGE_KEY));
      if (Number.isFinite(storedEndTime) && storedEndTime > now) return storedEndTime;
    } catch {
      // Storage can be unavailable in privacy-restricted browsing contexts.
    }

    return this.#reset(now);
  }

  #reset(now = Date.now()) {
    const nextEndTime = now + this.duration;

    try {
      sessionStorage.setItem(DIETZ_PROMO_COUNTDOWN_STORAGE_KEY, `${nextEndTime}`);
    } catch {
      // The countdown remains functional for this page without persistence.
    }

    return nextEndTime;
  }

  #start() {
    if (document.hidden || this.#frameId !== undefined) return;
    this.#frameId = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#frameId === undefined) return;
    cancelAnimationFrame(this.#frameId);
    this.#frameId = undefined;
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
      return;
    }

    this.#start();
  };

  #tick = () => {
    const now = Date.now();
    if (now >= this.#endAt) this.#endAt = this.#reset(now);

    const remaining = Math.max(0, this.#endAt - now);
    const hours = Math.floor(remaining / 3_600_000);
    const minutes = Math.floor((remaining % 3_600_000) / 60_000);
    const seconds = Math.floor((remaining % 60_000) / 1000);

    this.#updatePart('hours', hours, 2);
    this.#updatePart('minutes', minutes, 2);
    this.#updatePart('seconds', seconds, 2);

    const accessibleSecond = Math.ceil(remaining / 1000);
    if (accessibleSecond !== this.#lastAccessibleSecond) {
      this.#lastAccessibleSecond = accessibleSecond;
      const isEnglish = (this.dataset.locale ?? '').toLowerCase().startsWith('en');
      this.setAttribute(
        'aria-label',
        isEnglish
          ? `${hours} hours, ${minutes} minutes and ${seconds} seconds remaining`
          : `${hours} horas, ${minutes} minutos y ${seconds} segundos restantes`
      );
    }

    this.#frameId = requestAnimationFrame(this.#tick);
  };

  #updatePart(name, value, length) {
    const part = this.#parts[name];
    if (!part) return;

    const nextValue = `${value}`.padStart(length, '0');
    if (part.textContent !== nextValue) part.textContent = nextValue;
  }
}

if (!customElements.get('dietz-promo-countdown')) {
  customElements.define('dietz-promo-countdown', DietzPromoCountdown);
}
